import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "./AuthContext";

export default function LineupManager({ eventId }) {
  const [speakers, setSpeakers] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await adminApi.get(`/events/${eventId}/speakers`);
      setSpeakers(data.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const move = async (sp, delta) => {
    // simple swap with neighbour — robust enough for show-night reordering
    const idx = speakers.findIndex((s) => s.id === sp.id);
    const swap = speakers[idx + delta];
    if (!swap) return;
    try {
      await Promise.all([
        adminApi.patch(`/speakers/${sp.id}`, { performance_order: swap.performance_order }),
        adminApi.patch(`/speakers/${swap.id}`, { performance_order: sp.performance_order }),
      ]);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div style={{ marginTop: 28 }} data-testid={`lineup-section-${eventId}`}>
      <h3 style={{ fontSize: 13, letterSpacing: 1, margin: "0 0 10px 0", opacity: 0.8 }}>
        SPEAKER LINEUP
      </h3>
      <CreateSpeakerForm eventId={eventId} onCreated={load} nextOrder={speakers.length} />
      {err && (
        <div style={{ color: "#ff6b6b", margin: "10px 0", fontSize: 13 }} data-testid={`lineup-error-${eventId}`}>
          {err}
        </div>
      )}
      {loading ? (
        <div>Loading lineup…</div>
      ) : speakers.length === 0 ? (
        <div style={{ opacity: 0.6, marginTop: 10, fontSize: 13 }}>
          No speakers yet. Add the running order above.
        </div>
      ) : (
        <table data-testid={`lineup-table-${eventId}`} style={tableStyle}>
          <thead>
            <tr style={trHead}>
              <th style={{ ...th, width: 60 }}>#</th>
              <th style={th}>NAME</th>
              <th style={th}>TALK TITLE</th>
              <th style={th}>LINKED CONTACT</th>
              <th style={th}>SCORING</th>
              <th style={th}>ACTIVE</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {speakers.map((sp, i) => (
              <SpeakerRow
                key={sp.id}
                sp={sp}
                onReload={load}
                canMoveUp={i > 0}
                canMoveDown={i < speakers.length - 1}
                onMove={(d) => move(sp, d)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SpeakerRow({ sp, onReload, canMoveUp, canMoveDown, onMove }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(sp.display_name);
  const [title, setTitle] = useState(sp.talk_title || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await adminApi.patch(`/speakers/${sp.id}`, {
        display_name: name.trim(),
        talk_title: title.trim(),
      });
      setEditing(false);
      onReload();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await adminApi.patch(`/speakers/${sp.id}`, { active: !sp.active });
      onReload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr data-testid={`speaker-row-${sp.id}`} style={{ ...trBody, opacity: sp.active ? 1 : 0.5 }}>
      <td style={td}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <button
            data-testid={`speaker-up-${sp.id}`}
            onClick={() => onMove(-1)}
            disabled={!canMoveUp}
            style={{ ...btnMini, opacity: canMoveUp ? 1 : 0.3 }}
            title="Move up"
          >
            ▲
          </button>
          <span style={{ fontWeight: 700, fontSize: 13, textAlign: "center" }}>
            {sp.performance_order + 1}
          </span>
          <button
            data-testid={`speaker-down-${sp.id}`}
            onClick={() => onMove(1)}
            disabled={!canMoveDown}
            style={{ ...btnMini, opacity: canMoveDown ? 1 : 0.3 }}
            title="Move down"
          >
            ▼
          </button>
        </div>
      </td>
      <td style={td}>
        {editing ? (
          <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        ) : (
          sp.display_name
        )}
      </td>
      <td style={td}>
        {editing ? (
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
            placeholder="(optional)"
          />
        ) : (
          sp.talk_title || <span style={{ opacity: 0.4 }}>—</span>
        )}
      </td>
      <td style={td}>
        {sp.contact_id ? (
          <span style={{ fontSize: 11, fontFamily: "monospace", opacity: 0.7 }}>
            {sp.contact_id.slice(-8)}
          </span>
        ) : (
          <span style={{ opacity: 0.4 }}>—</span>
        )}
      </td>
      <td style={td}>
        <Pill state={sp.scoring_state} />
      </td>
      <td style={td}>
        <span style={{ color: sp.active ? "#5dd6a4" : "#ff6b6b" }}>
          {sp.active ? "yes" : "no"}
        </span>
      </td>
      <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
        {editing ? (
          <>
            <button
              data-testid={`speaker-save-${sp.id}`}
              onClick={save}
              disabled={busy}
              style={{ ...btnPrimary, padding: "4px 10px", fontSize: 12 }}
            >
              {busy ? "…" : "Save"}
            </button>{" "}
            <button onClick={() => setEditing(false)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              data-testid={`speaker-edit-${sp.id}`}
              onClick={() => setEditing(true)}
              style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}
            >
              Edit
            </button>{" "}
            <button
              data-testid={`speaker-toggle-${sp.id}`}
              onClick={toggleActive}
              disabled={busy}
              style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}
            >
              {sp.active ? "Deactivate" : "Reactivate"}
            </button>
          </>
        )}
        {err && <div style={{ color: "#ff6b6b", fontSize: 11, marginTop: 4 }}>{err}</div>}
      </td>
    </tr>
  );
}

function CreateSpeakerForm({ eventId, onCreated, nextOrder }) {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const q = contactQuery.trim();
    if (q.length < 2) {
      setContactResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi.get("/admin/contacts/search", {
          params: { q },
        });
        if (!cancelled) setContactResults(data.items || []);
      } catch {
        /* ignore */
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [contactQuery]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await adminApi.post(`/events/${eventId}/speakers`, {
        display_name: name.trim(),
        talk_title: title.trim() || undefined,
        contact_id: selectedContact?.id || undefined,
      });
      setName("");
      setTitle("");
      setContactQuery("");
      setContactResults([]);
      setSelectedContact(null);
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`lineup-create-form-${eventId}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1.6fr 1.4fr auto",
        gap: 10,
        alignItems: "end",
        padding: 12,
        background: "#0A0F1F",
        border: "1px solid #14213D",
      }}
    >
      <Field label={`Display name (slot #${nextOrder + 1})`}>
        <input
          data-testid={`lineup-create-name-${eventId}`}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Talk title (optional)">
        <input
          data-testid={`lineup-create-title-${eventId}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Link existing contact (optional)">
        {selectedContact ? (
          <div
            data-testid={`lineup-selected-contact-${eventId}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px",
              background: "#101C35",
              border: "1px solid #2a3556",
            }}
          >
            <span style={{ fontSize: 12, flex: 1 }}>
              {selectedContact.name}{" "}
              <span style={{ opacity: 0.6 }}>· {selectedContact.email}</span>
            </span>
            <button
              type="button"
              onClick={() => setSelectedContact(null)}
              style={{ ...btnMini, padding: "2px 6px" }}
            >
              ×
            </button>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <input
              data-testid={`lineup-contact-search-${eventId}`}
              value={contactQuery}
              onChange={(e) => setContactQuery(e.target.value)}
              placeholder="Type 2+ chars to search…"
              style={inputStyle}
            />
            {contactResults.length > 0 && (
              <ul
                data-testid={`lineup-contact-results-${eventId}`}
                style={{
                  position: "absolute",
                  zIndex: 5,
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "#101C35",
                  border: "1px solid #2a3556",
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  maxHeight: 180,
                  overflowY: "auto",
                }}
              >
                {contactResults.map((c) => (
                  <li
                    key={c.id}
                    onClick={() => {
                      setSelectedContact(c);
                      setContactQuery("");
                      setContactResults([]);
                    }}
                    style={{ padding: "6px 10px", cursor: "pointer", fontSize: 12 }}
                  >
                    {c.name} <span style={{ opacity: 0.6 }}>· {c.email}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Field>
      <button
        data-testid={`lineup-create-submit-${eventId}`}
        type="submit"
        disabled={busy || !name.trim()}
        style={{ ...btnPrimary, height: 36 }}
      >
        {busy ? "Adding…" : "Add speaker"}
      </button>
      {err && (
        <div
          style={{ gridColumn: "1 / -1", color: "#ff6b6b", fontSize: 12 }}
          data-testid={`lineup-create-error-${eventId}`}
        >
          {err}
        </div>
      )}
    </form>
  );
}

function Pill({ state }) {
  const colors = { pending: "#9aa8c4", open: "#5dd6a4", closed: "#E5B25D" };
  return (
    <span
      style={{
        padding: "2px 8px",
        background: colors[state] || "#9aa8c4",
        color: "#0A0F1F",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
      }}
    >
      {String(state || "pending").toUpperCase()}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ fontSize: 11, letterSpacing: 1, opacity: 0.7 }}>
      {label.toUpperCase()}
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  background: "#0A0F1F",
  border: "1px solid #2a3556",
  color: "#fff",
  borderRadius: 2,
  marginTop: 4,
  fontSize: 13,
  boxSizing: "border-box",
};
const btnPrimary = {
  padding: "8px 14px",
  background: "#E5B25D",
  color: "#0A0F1F",
  border: 0,
  fontWeight: 700,
  cursor: "pointer",
};
const btnSecondary = {
  padding: "8px 14px",
  background: "transparent",
  color: "#fff",
  border: "1px solid #2a3556",
  cursor: "pointer",
};
const btnMini = {
  padding: "2px 6px",
  background: "transparent",
  color: "#fff",
  border: "1px solid #2a3556",
  cursor: "pointer",
  fontSize: 10,
};
const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 12 };
const th = { padding: "10px 8px", fontWeight: 600 };
const td = { padding: "10px 8px", fontSize: 14, verticalAlign: "top" };
const trHead = { textAlign: "left", fontSize: 11, opacity: 0.6 };
const trBody = { borderTop: "1px solid #14213D" };
